import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { createIntl, createIntlCache, type MessageFormatElement } from 'react-intl-next';
import { ref } from 'valtio';

import { i18n } from '@canvas/intl';

import { getExternalDatabasesMessages } from './external-messages/get-external-databases-messages';
import {
  fetchLocaleMessages,
  getExternalMessagesPromiseFromLoaders
} from './providers/helpers';

type IntlMessages =
  | Record<string, string>
  | Record<string, MessageFormatElement[]>
  | undefined;

const potentiallyResolvedMessages = new Map<string, IntlMessages>();

function warnIntlMessageFallback(message: string, error: Error) {
  // eslint-disable-next-line no-console
  console.warn(message, error);
}

export function getResolvedIntlMessages(locale: string): IntlMessages | undefined {
  return potentiallyResolvedMessages.get(locale);
}

export async function fetchIntlMessages(locale: string): Promise<IntlMessages> {
  GlobalPageLoadExperience.mark('intl-load-start');

  const productMessagesPromise = fetchLocaleMessages(locale).catch((error: unknown) => {
    if (error instanceof Error) {
      warnIntlMessageFallback('Error loading product messages', error);
    }

    return {};
  });

  const externalMessagePromises = getExternalMessagesPromiseFromLoaders(
    getExternalDatabasesMessages(locale)
  );

  try {
    const [messages, ...external] = await Promise.all([
      productMessagesPromise,
      ...externalMessagePromises
    ]);

    let externalMessages = {};

    for (const { messages } of external) {
      externalMessages = { ...messages, ...externalMessages };
    }

    // Using createIntl is not currently advised.
    // Because comments is a whiteboards component with whiteboards translations,
    // this is a required workaround to have our translations work with comments.
    const cache = createIntlCache();

    i18n.intl = ref(
      createIntl(
        { locale, messages: { ...externalMessages, ...messages }, onError: () => {} },
        cache
      )
    );

    potentiallyResolvedMessages.set(locale, {
      ...externalMessages,
      ...messages
    });

    return potentiallyResolvedMessages.get(locale);

    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (error) {
    if (error instanceof Error) {
      warnIntlMessageFallback('Error loading messages', error);
    }
  } finally {
    GlobalPageLoadExperience.mark('intl-load-end');
  }
}
